import Modal from './typeManagement.modal.component';
import template from './typeManagement.component.html';

/**
 * @typedef {object} Type
 * @property {string} name
 * @property {string} description
 * @property {object} formSchema
 * @property {string} [model]
 * @property {string[]} groups
 * @property {object} data
 */

class TypesManagement {
  constructor(
    $state,
    $stateParams,
    $transitions,
    growl,
    ModalService,
    TypeService,
    Organization
  ) {
    'ngInject';

    this.$state = $state;
    this.growl = growl;
    this.$stateParams = $stateParams;
    this.hasOwnerOrg = false;
    this.service = {
      Type: TypeService,
      Modal: ModalService,
      Organization
    };

    this.formOptions = {
      buildFromSchema: true,
      enableBuilder: false,
      readOnly: true,
      hideAdvanced: true,
      title: 'Type Schema',
    };

    /** @type {Type[]} Copy of types store; updates when addType is called */
    this.typesArr = [];
    this.values = [];
    /**
     * Private model of types
     *
     * @constant
     * @type {Map}
     */
    this._types_ = new Map();

    // Nested route handling
    this.transitionHooks = [
      $transitions.onSuccess({ to: 'admin.types.type' }, () => {
        this.selected = this.$stateParams.typeId;
        this.hasOwnerOrg = Boolean(_.get(this.orgs, ['byId', this.selected?.org], false));
      }),
      $transitions.onSuccess({ to: 'admin.types.new' }, () => this.newType()),
      $transitions.onSuccess({ to: 'admin.types.edit' }, () => this.editType()),
    ];
  }

  async $onInit() {
    await this.fetchTypes();

    // If route has a typeId, set it as selected
    this.selected = this.$stateParams.typeId;
    this.orgs = await this.service.Organization.model;
    this.hasOwnerOrg = Boolean(_.get(this.orgs, ['byId', this.selected?.org], false));

    switch (this.$state.current.name) {
      case 'admin.types.type':
        break;
      case 'admin.types.new':
        this.newType();
        break;
      case 'admin.types.edit':
        this.editType();
        break;
      default:
    }
  }

  fetchTypes() {
    return this.service.Type
      .getAllTypes()
      .then(({ data: types }) => { this.addType(...types) });
  }


  /**
   * @readonly
   * @returns {Type[]} A list all types
   */
  get types() {
    return [...this._types_.values()];
  }

  /**
   * DON'T ASSIGN THIS; use openType modal instead
   *
   * @private
   * @param {string} typeId ID of type to select
   */
  set selected(typeId) {
    this._selected_ = typeId;
  }

  /**
   * @returns {Type|undefined} The currently selected Type, if any
   */
  get selected() {
    return this._types_.get(this._selected_);
  }

  /**
   * Opens the type page for a given typeId
   *
   * @param {string} [typeId] Type to open; defaults to selected type
   */
  openType(typeId = this._selected_) {
    this.$state.go('admin.types.type', { typeId });
  }

  /**
   * Opens the types page
   */
  openTypes() {
    this.$state.go('admin.types');
  }

  /**
   * Adds a type to the view modal
   * Don't call this; use the modal instead
   *
   * @private
   * @param {...Type} types Type(s) to add
   * @returns {undefined}
   */
  addType(...types) {
    types.forEach(type => this._types_.set(type.id, type));
    this.typesArr = _.sortBy(this.types, res => res.title.toLowerCase());
  }

  /**
   * Opens the modal for editing a type and submits changes
   *
   * @param {string} [typeId] Target - If omitted, targets selected type
   * @returns {Promise<undefined>} Result of modal
   */
  editType(typeId) {
    if (!typeId) {
      try {
        // Default to selected type ID, if any
        typeId = this.selected.id;
      } catch (err) {
        // This should never happen
        console.error('tried to edit without specifying type id', err);
      }
    }
    return this
      .openModal(typeId)
      .then(async (newType) => {
        try {
          const { data: [type] } = await this.service.Type.update(typeId, newType);
          const { groups } = newType;
          this.addType({ ...type, groups });
          this.growl.success(`Updated ${newType.name}`);
        } catch (err) {
          this.growl.error(err.error || err, { title: `Error updating ${newType.name}` });
        }
      })
      .catch(_.noop) // User closed modal
      .finally(() => this.openType(typeId));
  }

  /**
   * Opens the modal for creating a new type and submits it
   *
   * @returns {Promise<undefined>} Result of opening modal
   */
  newType() {
    return this
      .openModal()
      .then(async (newType) => {
        try {
          const { data: type } = await this.service.Type.create(newType);
          const { groups } = newType;
          this.addType({ ...type, groups });
          this.growl.success(type.name, { title: 'Created type' });
          this.openType(type.id);
        } catch (err) {
          this.growl.error(err.error || err, { title: `Unable to create ${newType.name}` });
        }
      })
      .catch(() => this.selected ? this.openType() : this.$state.go('admin.types'));
  }

  /**
   * Opens the modal for deleting a type and submits its request
   *
   * @param {string} typeId ID of type. Defaults to selected type, if omitted
   * @returns {Promise<undefined>} Result of opening confirmation modal
   * @throws If target type is not specified
   */
  destroyType(typeId = this.selected.id) {
    if (!typeId) {
      throw new TypeError('Tried to destroy without specifying or selecting target');
    }
    const { title } = this._types_.get(typeId);
    return this.service.Modal
      .open({
        component: 'confirmationModal',
        resolve: {
          modalOptions: {
            modalTitle: 'Delete Type',
            optionalWarning: 'This will delete the type and all of its resources from the platform.',
            selectionCriteria: `The type ${title} will be removed.`,
            buttonText: 'Proceed',
            danger: true
          }
        }
      })
      .then(async () => {
        try {
          await this.service.Type.destroy(typeId);
          this._types_.delete(typeId);
          this.growl.success(`Deleted ${title}`);
          await this.fetchTypes();
          this.openTypes();
        } catch (err) {
          this.growl.error(err.error || err, { title: `Unable to delete ${title}` });
        }
      })
      .catch(_.noop); // User closed modal
  }

  openModal(typeId) {
    return this.service.Modal.open({
      ...Modal,
      size: 'lg',
      resolve: {
        type: _.cloneDeep(this._types_.get(typeId))
      }
    });
  }

  $onDestroy() {
    this.transitionHooks.forEach(unregister => unregister()); // UI-router hack to prevent multiple modals
  }

}

export default {
  name: 'typeManagement',
  template,
  controller: TypesManagement,
  bindings: {}
};
